import type {ReactNode} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Text} from '@freckle/educator-materials/ts/components/typography'
import {label1, label2, label3, baseLabel} from './label.module.scss'

/**
 * label-1: block-level content, largest
 * label-2: inline-level content, slightly smaller
 * label-3: inline-level content, the smallest
 */
export type VariantT = 'label-1' | 'label-2' | 'label-3'

type Props = {
  /** Content of the Label */
  children?: ReactNode
  /** Additional CSS Class to add to the button */
  className?: string
  variant?: VariantT
  htmlFor?: string
  dataTest?: string
  as?: 'div' | 'p' | 'span' | 'h1'
}

/**
 * This is the standard component used for Labels.
 *
 */
function Label(props: Props) {
  const {children, variant = 'label-1', className, htmlFor, dataTest} = props
  const tag = props.as ?? 'span'

  const baseName = getClassName(variant)
  const newClassName = addMaybeClassName(baseName, className)
  const dataTestAttribute = dataTest !== undefined ? {'data-test': dataTest} : {}
  return (
    <label className={baseLabel} htmlFor={htmlFor} {...dataTestAttribute}>
      <Text style={variant} htmlElement={tag} className={newClassName}>
        {children}
      </Text>
    </label>
  )
}

function getClassName(variant: VariantT): string {
  switch (variant) {
    case 'label-1':
      return label1
    case 'label-2':
      return label2
    case 'label-3':
      return label3
    default:
      return exhaustive(variant)
  }
}

export {Label}
